import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import {
    mdiShareOutline, mdiPlus, mdiDragVertical, mdiContentCopy,
    mdiPencil, mdiTrashCan, mdiHelpCircleOutline, mdiHelpCircle, mdiLinkVariant,
    mdiFormatListChecks, mdiOpenInNew, mdiChevronDown, mdiChevronUp,
} from '@mdi/js';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import AppHeader from '../../components/AppHeader';
import ClassSettingsHelpModal from '../../components/ClassSettingsHelpModal';
import { classApi } from '../../api/classes';
import ConfirmationModal from '../../components/ConfirmationModal';
import TrialDealEditModal from '../../components/classes/TrialDealEditModal';
import TrialDealUrlPanel from '../../components/classes/TrialDealUrlPanel';
import { formatCurrencyFromMinor } from '../../utils/currency';
import '../Common.css';
import '../Dashboard.css';
import '../header.css';
import './ClassSettingsPage.css';
import '../../components/ClassesHelpModal.css';
/**
 * ClassSettingsPage - School URL management + URL groups (drag-to-reorder)
 * Portal type: Sub-Portal Feature (.sub-portal-header-card)
 */
const ClassSettingsPage = () => {
    const navigate = useNavigate();

    // --- School URL state ---
    const [orgSlug, setOrgSlug] = useState('');
    const [slugInput, setSlugInput] = useState('');
    const [isLoadingSlug, setIsLoadingSlug] = useState(true);
    const [isSavingSlug, setIsSavingSlug] = useState(false);
    const [shareTermId, setShareTermId] = useState('');

    // --- Help modal state ---
    const [helpOpen, setHelpOpen] = useState(false);
    const [helpIconHovered, setHelpIconHovered] = useState(false);

    // --- Groups ---
    const [groups, setGroups] = useState([]);
    const [isLoadingGroups, setIsLoadingGroups] = useState(true);
    const [showGroupForm, setShowGroupForm] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [formName, setFormName] = useState('');
    const [formSlug, setFormSlug] = useState('');
    const [isSavingGroup, setIsSavingGroup] = useState(false);

    // --- Class assignment modal ---
    const [allClasses, setAllClasses] = useState([]);
    const [allShortCourses, setAllShortCourses] = useState([]);
    const [managingGroup, setManagingGroup] = useState(null);
    const [selectedClassIds, setSelectedClassIds] = useState(new Set());
    const [selectedShortCourseIds, setSelectedShortCourseIds] = useState(new Set());
    const [isSavingGroupClasses, setIsSavingGroupClasses] = useState(false);

    // Drag state
    const dragIndexRef = useRef(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, variant, onConfirm }

    // --- Trial Deals ---
    const [trialDeals, setTrialDeals] = useState([]);
    const [isLoadingTrialDeals, setIsLoadingTrialDeals] = useState(true);
    const [trialDealModalOpen, setTrialDealModalOpen] = useState(false);
    const [editingTrialDeal, setEditingTrialDeal] = useState(null);
    const [expandedTrialDealId, setExpandedTrialDealId] = useState(null);

    const loadGroups = useCallback(async () => {
        setIsLoadingGroups(true);
        try {
            const r = await classApi.getGroups();
            if (r.ok) setGroups(r.data.groups || []);
            else toast.error(r.data?.message || 'Failed to load groups');
        } catch {
            toast.error('Failed to load groups');
        } finally {
            setIsLoadingGroups(false);
        }
    }, []);

    const [allTerms, setAllTerms] = useState([]);
    const [shareTerms, setShareTerms] = useState([]);

    const loadClasses = useCallback(async () => {
        try {
            const [tr, sr] = await Promise.all([
                classApi.getTerms(),
                classApi.getShortCourses(),
            ]);
            if (tr.ok) {
                // All non-past terms power the share dropdown (closed ones shown disabled).
                setShareTerms((tr.data.terms || []).filter(t => !t.isPast));
                // Only enrolment-open terms drive the classes shown on this page.
                const nonPastTerms = (tr.data.terms || []).filter(t => !t.isPast && t.enrolmentOpen);
                setAllTerms(nonPastTerms);
                const classResults = await Promise.all(nonPastTerms.map(t => classApi.getClasses(t.id)));
                const combined = classResults.flatMap((r, i) =>
                    r.ok ? (r.data.classes || []).map(c => ({ ...c, termId: nonPastTerms[i].id, termName: nonPastTerms[i].name })) : []
                );
                setAllClasses(combined);
            }
            if (sr.ok) setAllShortCourses(sr.data.shortCourses || []);
        } catch { /* non-critical, ignore */ }
    }, []);

    const loadSchoolUrl = useCallback(async () => {
        setIsLoadingSlug(true);
        try {
            const r = await classApi.getSchoolUrl();
            if (r.ok) {
                const slug = r.data.orgSlug || '';
                setOrgSlug(slug);
                setSlugInput(slug);
                // Auto-save the suggested URL so it's immediately usable
                if (r.data.isAutoGenerated && slug) {
                    await classApi.updateSchoolUrl(slug);
                }
            } else {
                toast.error(r.data?.message || 'Failed to load school URL');
            }
        } catch {
            toast.error('Failed to load school URL');
        } finally {
            setIsLoadingSlug(false);
        }
    }, []);

    const loadTrialDeals = useCallback(async () => {
        setIsLoadingTrialDeals(true);
        try {
            const r = await classApi.getTrialDeals();
            if (r.ok) {
                const now = new Date();
                const deals = r.data.trialDeals || [];
                deals.sort((a, b) => {
                    const aExp = a.endDate && new Date(a.endDate) < now;
                    const bExp = b.endDate && new Date(b.endDate) < now;
                    if (aExp !== bExp) return aExp ? 1 : -1;
                    return 0;
                });
                setTrialDeals(deals);
            }
            else toast.error(r.data?.message || 'Failed to load trial deals');
        } catch {
            toast.error('Failed to load trial deals');
        } finally {
            setIsLoadingTrialDeals(false);
        }
    }, []);

    useEffect(() => {
        loadGroups();
        loadSchoolUrl();
        loadClasses();
        loadTrialDeals();
    }, [loadGroups, loadSchoolUrl, loadClasses, loadTrialDeals]);

    // --- School URL handlers ---
    const handleSaveSlug = async () => {
        if (!slugInput.trim()) { toast.error('School URL cannot be empty'); return; }
        setIsSavingSlug(true);
        try {
            const r = await classApi.updateSchoolUrl(slugInput.trim());
            if (r.ok) {
                setOrgSlug(r.data.orgSlug);
                setSlugInput(r.data.orgSlug);
                toast.success('School URL saved!');
            } else {
                toast.error(r.data?.message || 'Failed to save school URL');
            }
        } catch {
            toast.error('Failed to save school URL');
        } finally {
            setIsSavingSlug(false);
        }
    };

    // Permanent link — always shows whatever term is current (no param, self-updating).
    const schoolUrl = `${window.location.origin}/school/${orgSlug}`;
    // Optional link pinned to one specific open term.
    const termUrl = shareTermId ? `${window.location.origin}/school/${orgSlug}?term=${shareTermId}` : '';
    // Upcoming terms that can actually be shared (enrolment open, not the current term).
    const shareableUpcomingTerms = shareTerms.filter(t => !t.isCurrent && t.enrolmentOpen);

    const copyToClipboard = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            toast.success('URL copied to clipboard!');
        });
    };

    // --- Class assignment modal ---
    const handleOpenManage = (group) => {
        setManagingGroup(group);
        setSelectedClassIds(new Set(group.classIds || []));
        setSelectedShortCourseIds(new Set(group.shortCourseIds || []));
    };

    const handleToggleClass = (id) => {
        setSelectedClassIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleToggleShortCourse = (id) => {
        setSelectedShortCourseIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleSaveGroupClasses = async () => {
        if (!managingGroup) return;
        setIsSavingGroupClasses(true);
        try {
            const r = await classApi.updateGroup(managingGroup.id, {
                classIds: [...selectedClassIds],
                shortCourseIds: [...selectedShortCourseIds],
            });
            if (r.ok) {
                toast.success('Classes updated');
                setManagingGroup(null);
                loadGroups();
            } else {
                toast.error(r.data?.message || 'Failed to update classes');
            }
        } catch {
            toast.error('Failed to update classes');
        } finally {
            setIsSavingGroupClasses(false);
        }
    };

    // --- Group drag-to-reorder ---
    const handleDragStart = (index) => {
        dragIndexRef.current = index;
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleDrop = async (e, index) => {
        e.preventDefault();
        setDragOverIndex(null);
        const from = dragIndexRef.current;
        if (from === null || from === index) return;

        const reordered = [...groups];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(index, 0, moved);
        setGroups(reordered);
        dragIndexRef.current = null;

        // Group order is display-only; no persistence endpoint exists yet
    };

    // --- Copy group URL ---
    const handleCopyUrl = (group) => {
        const url = `${window.location.origin}/school/${orgSlug}/groups/${group.slug || group.id}`;
        navigator.clipboard.writeText(url).then(() => {
            toast.success('Group URL copied!');
        });
    };

    // --- Group form ---
    const openCreateForm = () => {
        setEditingGroup(null);
        setFormName('');
        setFormSlug('');
        setShowGroupForm(true);
    };

    const openEditForm = (group) => {
        setEditingGroup(group);
        setFormName(group.name);
        setFormSlug(group.slug || '');
        setShowGroupForm(true);
    };

    const handleSaveGroup = async () => {
        if (!formName) { toast.error('Group name is required'); return; }
        setIsSavingGroup(true);
        try {
            const data = { name: formName, slug: formSlug || undefined };
            const r = editingGroup
                ? await classApi.updateGroup(editingGroup.id, data)
                : await classApi.createGroup(data);
            if (r.ok) {
                toast.success(editingGroup ? 'Group updated' : 'Group created');
                setShowGroupForm(false);
                loadGroups();
            } else {
                toast.error(r.data?.message || 'Failed to save group');
            }
        } catch {
            toast.error('Failed to save group');
        } finally {
            setIsSavingGroup(false);
        }
    };

    const handleDeleteGroup = (group) => {
        setConfirmModal({
            title: 'Delete Group',
            message: `Delete group "${group.name}"? This cannot be undone.`,
            variant: 'danger',
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    const r = await classApi.deleteGroup(group.id);
                    if (r.ok) {
                        toast.success('Group deleted');
                        loadGroups();
                    } else {
                        toast.error(r.data?.message || 'Failed to delete group');
                    }
                } catch {
                    toast.error('Failed to delete group');
                }
            },
        });
    };

    const handleDeleteTrialDeal = (deal) => {
        setConfirmModal({
            title: 'Delete Trial Deal',
            message: `Delete "${deal.name}"? This cannot be undone.`,
            variant: 'danger',
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    const r = await classApi.deleteTrialDeal(deal.id);
                    if (r.ok) {
                        toast.success('Trial deal deleted');
                        loadTrialDeals();
                    } else {
                        toast.error(r.data?.message || 'Failed to delete trial deal');
                    }
                } catch {
                    toast.error('Failed to delete trial deal');
                }
            },
        });
    };

    return (
        <>
            <div className="dashboard-container org-console">
                <AppHeader />

                <div className="dashboard-main">
                    <div className="dashboard-content-wrapper">
                        <div className="sub-portal-header-card">
                            <div className="sub-portal-header-title-row">
                                <button onClick={() => navigate('/classes/manage')} className="back-nav-btn">
                                    ← Manage Classes
                                </button>
                                <h1 className="sub-portal-header-title">
                                    <Icon path={mdiShareOutline} size={1} color="var(--dl-color-purple-default)" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                    Promote
                                </h1>
                            </div>
                            <div className="classes-subtitle-row">
                                <p className="sub-portal-header-subtitle">
                                    Manage your school URL, URL groups, and trial deals.
                                </p>
                                <button
                                    className="classes-help-trigger"
                                    onClick={() => setHelpOpen(true)}
                                    onMouseEnter={() => setHelpIconHovered(true)}
                                    onMouseLeave={() => setHelpIconHovered(false)}
                                    aria-label="Class Settings Help"
                                    title="How It Works">
                                    <Icon
                                        path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                                        size="1.62rem"
                                        color="var(--dl-color-purple-default)"
                                    />
                                </button>
                            </div>
                        </div>

                        {/* School URL */}
                        <div className="content-card class-settings-section">
                            <div className="class-settings-groups-bar">
                                <h3 className="class-settings-section-title" style={{ margin: 0 }}>Get URL for your school</h3>
                            </div>
                            {isLoadingSlug ? (
                                <p className="class-settings-loading">Loading school URL...</p>
                            ) : (
                                <>
                                    <p className="class-settings-section-hint">
                                        Share these links with prospective students. They open your public class pages.
                                    </p>

                                    {/* Permanent school link — always shows the current term */}
                                    <label className="class-settings-form-label" style={{ marginTop: 0 }}>School link (always shows your current term)</label>
                                    <p className="class-settings-form-hint">Leave this one on your website. It always shows whatever term is running now, so you never need to update it.</p>
                                    <div className="class-settings-school-url-row">
                                        <div className="class-settings-school-url-display">
                                            <Icon path={mdiLinkVariant} size={0.9} color="#888" />
                                            <span className="class-settings-school-url-text">
                                                {orgSlug ? schoolUrl : `${window.location.origin}/school/…`}
                                            </span>
                                            <button
                                                className="class-settings-icon-btn copy"
                                                onClick={() => copyToClipboard(schoolUrl)}
                                                disabled={!orgSlug}
                                                title="Copy school link">
                                                <Icon path={mdiContentCopy} size={0.8} />
                                            </button>
                                        </div>
                                        <button
                                            className="class-settings-view-landing-btn"
                                            onClick={() => window.open(schoolUrl, '_blank')}
                                            disabled={!orgSlug}>
                                            <Icon path={mdiOpenInNew} size={0.8} />
                                            View landing page
                                        </button>
                                    </div>

                                    {/* Optional link pinned to a specific upcoming open term */}
                                    <label className="class-settings-form-label">Link to a specific term (optional)</label>
                                    {shareableUpcomingTerms.length > 0 ? (
                                        <>
                                            <p className="class-settings-form-hint">Promoting a term that hasn't started yet? Pick it here to link visitors straight to it. Only terms with enrolment open can be shared.</p>
                                            <select
                                                id="shareTermSelect"
                                                className="class-settings-form-input class-settings-share-term-select"
                                                value={shareTermId}
                                                onChange={e => setShareTermId(e.target.value)}>
                                                <option value="">Select a term…</option>
                                                {shareableUpcomingTerms.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                            </select>
                                            {termUrl && (
                                                <>
                                                    <div className="class-settings-school-url-row">
                                                        <div className="class-settings-school-url-display">
                                                            <Icon path={mdiLinkVariant} size={0.9} color="#888" />
                                                            <span className="class-settings-school-url-text">{termUrl}</span>
                                                            <button
                                                                className="class-settings-icon-btn copy"
                                                                onClick={() => copyToClipboard(termUrl)}
                                                                title="Copy term link">
                                                                <Icon path={mdiContentCopy} size={0.8} />
                                                            </button>
                                                        </div>
                                                        <button
                                                            className="class-settings-view-landing-btn"
                                                            onClick={() => window.open(termUrl, '_blank')}>
                                                            <Icon path={mdiOpenInNew} size={0.8} />
                                                            View landing page
                                                        </button>
                                                    </div>
                                                    <p className="class-settings-form-hint">
                                                        If you close this term's enrolment later, the link still works. Visitors see a "check back soon" message instead of the classes.
                                                    </p>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <p className="class-settings-form-hint">
                                            No upcoming terms with enrolment open. Create a future term and open its enrolment to share a direct link to it.
                                        </p>
                                    )}
                                    <label className="class-settings-form-label" htmlFor="orgSlugInput">Custom URL name</label>
                                    <p className="class-settings-form-hint">Lowercase letters, numbers, and hyphens only. Changing this will break any links you have already shared.</p>
                                    <div className="class-settings-slug-row">
                                        <input
                                            id="orgSlugInput"
                                            className="class-settings-form-input"
                                            value={slugInput}
                                            onChange={e => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                            placeholder="e.g. salsa-studio-sydney"
                                        />
                                        <button
                                            className="class-settings-add-btn"
                                            onClick={handleSaveSlug}
                                            disabled={isSavingSlug || slugInput === orgSlug}>
                                            {isSavingSlug ? 'Saving…' : 'Save'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Trial Deals */}
                        <div className="content-card class-settings-section">
                            <div className="class-settings-groups-bar">
                                <h3 className="class-settings-section-title" style={{ margin: 0 }}>Trial Deals</h3>
                                <button className="class-settings-add-btn" onClick={() => { setEditingTrialDeal(null); setTrialDealModalOpen(true); }}>
                                    <Icon path={mdiPlus} size={0.9} /> New Trial Deal
                                </button>
                            </div>
                            <div className="class-settings-info-box">
                                <strong>What is a Trial Deal?</strong> A discounted first-class offer for new students. You create the deal here, set the price and which classes it covers, then share the link on social media. Prospective students click the link, pay online, and appear on your check-in screen ready to attend. Each deal has its own set of tracked links (Facebook, Instagram, Google Ads, Email) so you can see which channel is driving sign-ups.
                            </div>
                            {isLoadingTrialDeals ? (
                                <p className="class-settings-loading">Loading trial deals...</p>
                            ) : trialDeals.length === 0 ? (
                                <p className="class-settings-empty">No trial deals yet. Create one to get a shareable trial landing page.</p>
                            ) : (
                                <div className="class-settings-groups-list">
                                    {trialDeals.map(deal => {
                                        const isExpanded = expandedTrialDealId === deal.id;
                                        const isExpiredDeal = deal.endDate && new Date(deal.endDate) < new Date();
                                        return (
                                            <div
                                                key={deal.id}
                                                className="class-settings-group-row"
                                                style={{ flexDirection: 'column', alignItems: 'stretch', cursor: 'default' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <div className="class-settings-group-info">
                                                        <div className="class-settings-group-name">{deal.name}</div>
                                                        <div className="class-settings-group-url">
                                                            {window.location.origin}/trial/{deal.slug}
                                                        </div>
                                                        <div className="class-settings-group-class-count">
                                                            <span className={`cs-deal-status-pill ${isExpiredDeal ? 'expired' : 'live'}`}>
                                                                {isExpiredDeal ? 'Expired' : 'Live'}
                                                            </span>
                                                            <span className="cs-deal-meta-text">
                                                                {formatCurrencyFromMinor(deal.price, deal.currency)}
                                                                {deal.endDate ? ` · ends ${DateTime.fromISO(deal.endDate).toFormat('EEE, d MMM yyyy')}` : ''}
                                                            </span>
                                                            <span className="cs-deal-meta-text">
                                                                Sold: {deal.purchaseCount || 0} · {formatCurrencyFromMinor(deal.totalRevenue, deal.currency)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="class-settings-group-actions">
                                                        <button
                                                            className="class-settings-icon-btn manage"
                                                            onClick={() => setExpandedTrialDealId(isExpanded ? null : deal.id)}
                                                            title={isExpanded ? 'Hide URLs' : 'Show URLs'}>
                                                            <Icon path={isExpanded ? mdiChevronUp : mdiChevronDown} size={0.8} />
                                                        </button>
                                                        <button
                                                            className="class-settings-icon-btn"
                                                            onClick={() => { setEditingTrialDeal(deal); setTrialDealModalOpen(true); }}
                                                            title="Edit">
                                                            <Icon path={mdiPencil} size={0.8} />
                                                        </button>
                                                        <button
                                                            className="class-settings-icon-btn danger"
                                                            onClick={() => handleDeleteTrialDeal(deal)}
                                                            title="Delete">
                                                            <Icon path={mdiTrashCan} size={0.8} />
                                                        </button>
                                                    </div>
                                                </div>
                                                {isExpanded && (
                                                    <div style={{ marginTop: 12 }}>
                                                        <TrialDealUrlPanel trialDeal={deal} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* URL Groups */}
                        <div className="content-card class-settings-section">
                            <div className="class-settings-groups-bar">
                                <h3 className="class-settings-section-title" style={{ margin: 0 }}>Get URLs for class groups</h3>
                                <button className="class-settings-add-btn" onClick={openCreateForm}>
                                    <Icon path={mdiPlus} size={0.9} /> New Group
                                </button>
                            </div>
                            <p className="class-settings-section-hint">
                                Your school URL already shows all your classes and short courses. Create a group to make a focused page — for example, just your Salsa classes, or just short courses. Click the list icon on any group to choose which classes appear on it.
                            </p>

                            {isLoadingGroups ? (
                                <p className="class-settings-loading">Loading groups...</p>
                            ) : groups.length === 0 ? (
                                <p className="class-settings-empty">No groups yet. Create a group to get a shareable landing page URL.</p>
                            ) : (
                                <div className="class-settings-groups-list">
                                    {groups.map((group, index) => (
                                        <div
                                            key={group.id}
                                            className={`class-settings-group-row${dragOverIndex === index ? ' drag-over' : ''}`}
                                            draggable
                                            onDragStart={() => handleDragStart(index)}
                                            onDragOver={(e) => handleDragOver(e, index)}
                                            onDrop={(e) => handleDrop(e, index)}
                                            onDragEnd={() => setDragOverIndex(null)}
                                        >
                                            <span className="class-settings-drag-handle">
                                                <Icon path={mdiDragVertical} size={0.9} />
                                            </span>
                                            <div className="class-settings-group-info">
                                                <div className="class-settings-group-name">{group.name}</div>
                                                <div className="class-settings-group-url">
                                                    {window.location.origin}/school/{orgSlug || '…'}/groups/{group.slug || group.id}
                                                </div>
                                                <div className="class-settings-group-class-count">
                                                    {(group.classIds?.length || 0) + (group.shortCourseIds?.length || 0)} classes assigned
                                                </div>
                                            </div>
                                            <div className="class-settings-group-actions">
                                                <button className="class-settings-icon-btn manage"
                                                    onClick={() => handleOpenManage(group)} title="Assign classes">
                                                    <Icon path={mdiFormatListChecks} size={0.8} />
                                                </button>
                                                <button className="class-settings-icon-btn copy"
                                                    onClick={() => handleCopyUrl(group)} title="Copy URL">
                                                    <Icon path={mdiContentCopy} size={0.8} />
                                                </button>
                                                <button className="class-settings-icon-btn"
                                                    onClick={() => openEditForm(group)} title="Edit name">
                                                    <Icon path={mdiPencil} size={0.8} />
                                                </button>
                                                <button className="class-settings-icon-btn danger"
                                                    onClick={() => handleDeleteGroup(group)} title="Delete">
                                                    <Icon path={mdiTrashCan} size={0.8} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* Group form overlay */}
                {showGroupForm && (
                    <div className="class-settings-form-overlay" onClick={() => setShowGroupForm(false)}>
                        <div className="class-settings-form" onClick={e => e.stopPropagation()}>
                            <h3>{editingGroup ? 'Edit Group' : 'New Group'}</h3>

                            <label className="class-settings-form-label">Name *</label>
                            <input className="class-settings-form-input" value={formName}
                                onChange={e => setFormName(e.target.value)} placeholder="e.g. Salsa Classes" />

                            <label className="class-settings-form-label">Custom URL name</label>
                            <p className="class-settings-form-hint">Leave blank to auto-generate from name</p>
                            <input className="class-settings-form-input" value={formSlug}
                                onChange={e => setFormSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                placeholder="e.g. salsa-classes" />

                            <div className="class-settings-form-actions">
                                <button className="class-settings-form-cancel" onClick={() => setShowGroupForm(false)}>Cancel</button>
                                <button className="class-settings-form-save" onClick={handleSaveGroup} disabled={isSavingGroup}>
                                    {isSavingGroup ? 'Saving...' : 'Save Group'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* Class assignment modal */}
            {managingGroup && (
                <div className="class-settings-form-overlay" onClick={() => setManagingGroup(null)}>
                    <div className="class-settings-form class-settings-assign-form" onClick={e => e.stopPropagation()}>
                        <h3>Assign classes to "{managingGroup.name}"</h3>
                        <p className="class-settings-form-hint" style={{ marginTop: 0 }}>
                            Choose which weekly classes and short courses appear on this group's public page.
                        </p>

                        <div className="class-settings-assign-body">
                            <p className="class-settings-assign-section-label">Weekly Classes</p>
                            {allClasses.length === 0 ? (
                                <p className="class-settings-assign-empty">No weekly classes found. Add classes in the Classes section first.</p>
                            ) : allClasses.map(cls => (
                                <label key={cls.id} className="class-settings-assign-item">
                                    <input
                                        type="checkbox"
                                        checked={selectedClassIds.has(cls.id)}
                                        onChange={() => handleToggleClass(cls.id)}
                                    />
                                    <span className="class-settings-assign-name">{cls.name}</span>
                                    <span className="class-settings-assign-meta">
                                        {cls.style}{cls.level ? ` · ${cls.level}` : ''}{cls.dayOfWeek != null ? ` · ${'Sun,Mon,Tue,Wed,Thu,Fri,Sat'.split(',')[cls.dayOfWeek]}` : ''}{cls.startTime ? ` ${cls.startTime}` : ''}
                                    </span>
                                </label>
                            ))}

                            <p className="class-settings-assign-section-label" style={{ marginTop: 16 }}>Short Courses</p>
                            {allShortCourses.length === 0 ? (
                                <p className="class-settings-assign-empty">No short courses found. Add short courses first.</p>
                            ) : allShortCourses.map(sc => (
                                <label key={sc.id} className="class-settings-assign-item">
                                    <input
                                        type="checkbox"
                                        checked={selectedShortCourseIds.has(sc.id)}
                                        onChange={() => handleToggleShortCourse(sc.id)}
                                    />
                                    <span className="class-settings-assign-name">{sc.name}</span>
                                    <span className="class-settings-assign-meta">
                                        {sc.style}{sc.level ? ` · ${sc.level}` : ''}{sc.sessionCount ? ` · ${sc.sessionCount} sessions` : ''}
                                    </span>
                                </label>
                            ))}
                        </div>

                        <div className="class-settings-form-actions">
                            <button className="class-settings-form-cancel" onClick={() => setManagingGroup(null)}>Cancel</button>
                            <button className="class-settings-form-save" onClick={handleSaveGroupClasses} disabled={isSavingGroupClasses}>
                                {isSavingGroupClasses ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <ClassSettingsHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

            {confirmModal && (
                <ConfirmationModal
                    isOpen
                    title={confirmModal.title}
                    message={confirmModal.message}
                    variant={confirmModal.variant}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal(null)}
                />
            )}

            {trialDealModalOpen && (
                <TrialDealEditModal
                    isOpen={trialDealModalOpen}
                    onClose={() => { setTrialDealModalOpen(false); loadTrialDeals(); }}
                    trialDeal={editingTrialDeal}
                    allTerms={allTerms}
                    allClasses={allClasses}
                    onSaved={() => {
                        setTrialDealModalOpen(false);
                        loadTrialDeals();
                    }}
                />
            )}
        </>
    );
};

export default ClassSettingsPage;
